import { useEffect, useState } from 'react';
import { heroSlides, heroStats, navLinks } from '../data/siteData';
import useSwipe from '../hooks/useSwipe';
import AnimatedCounter from './AnimatedCounter';
import BrandMark from './BrandMark';
import './Hero.css';

function Hero() {
  const [index, setIndex] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const count = heroSlides.length;

  useEffect(() => {
    if (isPaused) return undefined;
    // The automatic timer keeps looping continuously on its own — only
    // manual next/prev (buttons, swipe) stop at the ends.
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, 10000);
    return () => clearInterval(timer);
  }, [count, isPaused]);

  // Bounded, not looping — stops at the first/last slide instead of
  // wrapping around, for the arrows, swipe, and auto-rotate alike.
  const prev = () => setIndex((i) => Math.max(i - 1, 0));
  const next = () => setIndex((i) => Math.min(i + 1, count - 1));
  const slide = heroSlides[index];
  const swipeHandlers = useSwipe(next, prev);

  return (
    <section
      id="top"
      className="hero"
      onPointerEnter={(e) => e.pointerType === 'mouse' && setIsPaused(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setIsPaused(false)}
      {...swipeHandlers}
    >
      <div className="hero-bg">
          {heroSlides.map((s, i) => (
            <img
              key={s.image}
              className={`hero-bg-image hero-bg-image-${i} ${i === index ? 'is-active' : ''}`}
              src={s.image}
              alt={s.badge}
            />
          ))}
          <div className="hero-bg-overlay" aria-hidden="true" />
        </div>

        <div className="hero-decor" aria-hidden="true">
          <span className="spark spark-a" />
          <span className="spark spark-b" />
          <span className="spark spark-c" />
        </div>

        <div className="hero-nav">
          <a href="#top" className="brand hero-anim" style={{ animationDelay: '0ms' }}>
            <span className="brand-spark brand-spark-a" aria-hidden="true" />
            <span className="brand-spark brand-spark-b" aria-hidden="true" />
            <BrandMark />
            <span className="brand-name">Sundaravel Group</span>
          </a>

          <nav className={`site-nav ${menuOpen ? 'is-open' : ''}`}>
            {navLinks.map((link, i) => (
              <a
                key={link.label}
                href={link.href}
                className="hero-anim"
                style={{ animationDelay: `${60 + i * 50}ms` }}
                target={link.external ? '_blank' : undefined}
                rel={link.external ? 'noopener noreferrer' : undefined}
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              className="btn btn-primary nav-cta hero-anim"
              style={{ animationDelay: `${60 + navLinks.length * 50}ms` }}
              onClick={() => setMenuOpen(false)}
            >
              Contact Us
            </a>
          </nav>

          <button
            className={`menu-toggle ${menuOpen ? 'is-open' : ''}`}
            aria-label="Toggle navigation menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        <button className="hero-arrow hero-arrow-left" onClick={prev} aria-label="Previous slide">
          &#8249;
        </button>
        <button className="hero-arrow hero-arrow-right" onClick={next} aria-label="Next slide">
          &#8250;
        </button>

        <div className="hero-content section-inner">
          <div className="hero-text-col" key={index}>
            <span className="hero-badge hero-anim" style={{ animationDelay: '0ms' }}>
              <span className="hero-badge-dot" />
              {slide.badge}
            </span>

            <h1 className="hero-heading">
              {slide.heading.map((line, i) => {
                const isAccent = i === slide.accentLine;
                const isGradient = isAccent && slide.accent?.includes('gradient');
                return (
                  <span
                    key={line}
                    className={`hero-heading-line hero-anim ${isGradient ? 'hero-heading-gradient' : ''}`}
                    style={{
                      animationDelay: `${100 + i * 90}ms`,
                      ...(isAccent && !isGradient ? { color: slide.accent } : undefined),
                      ...(isGradient ? { backgroundImage: slide.accent } : undefined),
                    }}
                  >
                    {line}
                  </span>
                );
              })}
            </h1>

            <p className="hero-text hero-anim" style={{ animationDelay: '380ms' }}>
              {slide.text}
            </p>

            <div className="hero-actions hero-anim" style={{ animationDelay: '460ms' }}>
              <a href="#about" className="btn btn-outline">
                Our Story
              </a>
            </div>

            <div className="hero-dots hero-anim" style={{ animationDelay: '540ms' }}>
              {heroSlides.map((s, i) => (
                <button
                  key={s.badge}
                  className={`hero-dot ${i === index ? 'is-active' : ''}`}
                  onClick={() => setIndex(i)}
                  aria-label={`Show slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="hero-stats">
          {heroStats.map((stat, i) => (
            <div className="hero-stat" key={stat.label} style={{ animationDelay: `${700 + i * 80}ms` }}>
              <div className="hero-stat-value">
                <AnimatedCounter value={stat.value} />
              </div>
              <div className="hero-stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>
  );
}

export default Hero;
